import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetAgreementQuery,
  useSaveAgreementMutation,
  useSignAgreementMutation,
  useResendAgreementMutation,
} from "../store/api/agreementsApi";
import {
  agreementResponse,
  downloadAgreement,
} from "../lib/agreementDownloads";
import AgreementPdfPage from "./AgreementPdfPage";
import AgreementInk from "./AgreementInk";
import { useToast } from "./Toast";
export default function AgreementEditor({ id, onClose }) {
  const token = useSelector((state) => state.auth.token),
    toast = useToast();
  const { data, isLoading, error } = useGetAgreementQuery(id, {
    pollingInterval: 3000,
  });
  const record = data?.data;
  const [save, { isLoading: saving }] = useSaveAgreementMutation(),
    [sign, { isLoading: signing }] = useSignAgreementMutation(),
    [resend] = useResendAgreementMutation();
  const [strokes, setStrokes] = useState([]),
    [notes, setNotes] = useState(""),
    [revision, setRevision] = useState(null),
    [signerName, setSignerName] = useState(""),
    [signature, setSignature] = useState([]),
    [consent, setConsent] = useState(false),
    [color, setColor] = useState("red"),
    [document, setDocument] = useState(null),
    [pdfError, setPdfError] = useState(""),
    [signedUrl, setSignedUrl] = useState(""),
    [dirty, setDirty] = useState(false);
  const initialized = useRef(false);
  useEffect(() => {
    if (record && !initialized.current) {
      initialized.current = true;
      setStrokes(record.strokes || []);
      setNotes(record.notes || "");
      setRevision(record.revision);
      setSignerName(record.booking.customerName);
    }
  }, [record]);
  const templateId = record?.templateId,
    status = record?.status;
  useEffect(() => {
    if (!templateId || !status) return;
    let cancelled = false,
      loadingTask,
      url;
    setPdfError("");
    (async () => {
      try {
        if (status === "signed") {
          const response = await agreementResponse(`/${id}/pdf`, token);
          url = URL.createObjectURL(await response.blob());
          if (!cancelled) setSignedUrl(url);
          return;
        }
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
        const assets = `${process.env.PUBLIC_URL || ""}/pdf-assets/${pdfjs.version}`;
        pdfjs.GlobalWorkerOptions.workerSrc = `${assets}/pdf.worker.min.js`;
        const response = await agreementResponse(
          `/templates/${templateId}/pdf`,
          token,
        );
        loadingTask = pdfjs.getDocument({
          data: new Uint8Array(await response.arrayBuffer()),
          isEvalSupported: false,
          enableXfa: false,
          standardFontDataUrl: `${assets}/standard_fonts/`,
          cMapUrl: `${assets}/cmaps/`,
          cMapPacked: true,
        });
        const pdf = await loadingTask.promise;
        if (!cancelled) setDocument(pdf);
      } catch {
        if (!cancelled)
          setPdfError(
            "Could not open the PDF. Please close and reopen the agreement.",
          );
      }
    })();
    return () => {
      cancelled = true;
      loadingTask?.destroy();
      if (url) URL.revokeObjectURL(url);
    };
  }, [templateId, status, id, token]);
  useEffect(() => {
    const handler = (event) => {
      if (dirty || signature.length) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, signature.length]);
  const fail = (error) =>
    toast.error(
      error?.data?.error?.message ||
        error.message ||
        "Could not complete this action.",
    );
  const close = () => {
    if (
      (dirty || signature.length) &&
      record?.status !== "signed" &&
      !window.confirm(
        "There are unsaved markings or a signature. Close without saving these changes?",
      )
    )
      return;
    onClose();
  };
  const saveDraft = async () => {
    try {
      const result = await save({ id, revision, strokes, notes }).unwrap();
      setRevision(result.data.revision);
      setDirty(false);
      toast.success(
        "Draft saved. The signature is only saved when you finalize.",
      );
    } catch (error) {
      fail(error);
    }
  };
  const finalize = async () => {
    try {
      await sign({
        id,
        revision,
        strokes,
        notes,
        signerName,
        signature,
        consent,
      }).unwrap();
      setDirty(false);
      setSignature([]);
      toast.success("Signed agreement saved. Email delivery is queued.");
    } catch (error) {
      fail(error);
    }
  };
  const busy = saving || signing,
    statusSigned = record?.status === "signed",
    editable = record?.status === "draft" && !busy;
  return (
    <div
      className="fixed inset-0 z-50 bg-gray-100 flex flex-col font-manrope-regular text-sm text-gray-700"
      role="dialog"
      aria-modal="true"
      aria-label="Vehicle agreement"
    >
      <header className="bg-white border-b p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-manrope-bold text-lg">
            Vehicle agreement · {record?.booking.bookingNumber || "Loading"}
          </h2>
          <p className="text-sm text-gray-500">
            {record?.booking.customerName} · {record?.booking.customerEmail}
          </p>
        </div>
        <div className="flex gap-3 items-center">
          {record && (
            <span className="capitalize text-sm">
              {record.status} · Email: {record.emailStatus.replace(/_/g, " ")}
            </span>
          )}
          <button
            disabled={busy}
            onClick={close}
            className="border px-4 py-2.5 rounded-xl text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 border-gray-200"
          >
            Close
          </button>
        </div>
      </header>
      <div className="overflow-y-auto flex-1 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {isLoading && <p>Loading agreement…</p>}
          {!!error && <p role="alert">Could not load the agreement.</p>}
          {pdfError && (
            <p role="alert" className="text-red-700">
              {pdfError}
            </p>
          )}
          {record && (
            <div className="bg-white rounded-xl p-4 mb-5 grid sm:grid-cols-2 gap-2 text-sm">
              <p>
                Trip: {record.booking.date} · {record.booking.timeSlot}
              </p>
              <p>Guests: {record.booking.quantity}</p>
              <p>Phone: {record.booking.customerPhone}</p>
              <p>Template version: {record.templateId.slice(0, 8)}</p>
            </div>
          )}
          {statusSigned ? (
            <>
              <div className="bg-green-50 rounded-xl p-4 mb-4 space-y-3">
                <p>
                  Signed by {record.signerName} on{" "}
                  {new Date(record.signedAt).toLocaleString()}. This document is
                  finalized and cannot be edited.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() =>
                      downloadAgreement(
                        `/${id}/pdf`,
                        `agreement-${record.booking.bookingNumber}.pdf`,
                        token,
                      ).catch(fail)
                    }
                    className="bg-primary text-white px-4 py-2.5 rounded-xl text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:opacity-90"
                  >
                    Download signed PDF
                  </button>
                  <button
                    disabled={record.emailStatus === "sending"}
                    onClick={() =>
                      resend(id)
                        .unwrap()
                        .then(() => toast.success("Email queued for delivery."))
                        .catch(fail)
                    }
                    className="border px-4 py-2.5 rounded-xl text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 border-gray-200"
                  >
                    Resend email
                  </button>
                </div>
                <p className="text-xs break-all">
                  File SHA-256: {record.sha256}
                </p>
              </div>
              {signedUrl && (
                <iframe
                  title="Signed agreement PDF"
                  src={signedUrl}
                  className="w-full h-[75vh] bg-white"
                />
              )}
            </>
          ) : (
            <>
              <div className="sticky top-0 z-10 bg-white rounded-xl shadow p-3 mb-4 flex flex-wrap items-center gap-3">
                <span className="text-sm font-manrope-bold">
                  Mark vehicle condition:
                </span>
                {["red", "blue", "green", "yellow"].map((item) => (
                  <button
                    key={item}
                    disabled={!editable}
                    onClick={() => setColor(item)}
                    aria-pressed={color === item}
                    aria-label={`${item} marking pen`}
                    className={`px-3 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 capitalize ${color === item ? "ring-2 ring-primary" : ""}`}
                  >
                    {item}
                  </button>
                ))}
                <button
                  disabled={!editable || !strokes.length}
                  onClick={() => {
                    setStrokes(strokes.slice(0, -1));
                    setDirty(true);
                  }}
                  className="text-sm underline text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
                >
                  Undo last mark
                </button>
                <button
                  disabled={!editable || !strokes.length}
                  onClick={() => {
                    if (window.confirm("Clear all condition markings?")) {
                      setStrokes([]);
                      setDirty(true);
                    }
                  }}
                  className="text-sm text-red-700 underline text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
                >
                  Clear marks
                </button>
              </div>
              {!document && !pdfError && <p>Opening PDF…</p>}
              {document &&
                Array.from({ length: document.numPages }, (_, index) => (
                  <AgreementPdfPage
                    key={index}
                    document={document}
                    pageNumber={index + 1}
                    onPageError={() =>
                      setPdfError(
                        "A PDF page could not render. Reopen the agreement before signing.",
                      )
                    }
                    strokes={strokes}
                    onChange={(value) => {
                      setStrokes(value);
                      setDirty(true);
                    }}
                    color={color}
                    disabled={!editable}
                  />
                ))}
              <div className="bg-white rounded-2xl p-5 space-y-4">
                <label className="block">
                  Vehicle condition notes
                  <textarea
                    aria-label="Vehicle condition notes"
                    maxLength={1500}
                    disabled={!editable}
                    value={notes}
                    onChange={(event) => {
                      setNotes(event.target.value);
                      setDirty(true);
                    }}
                    className="block border rounded-lg p-3 w-full mt-2"
                    rows={3}
                  />
                </label>
                <button
                  disabled={
                    !editable || revision === null || !document || !!pdfError
                  }
                  onClick={saveDraft}
                  className="border rounded-xl px-4 py-2.5 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 border-gray-200"
                >
                  {saving ? "Saving…" : "Save draft"}
                </button>
                <hr />
                <h3 className="font-manrope-bold text-lg">
                  Customer signature
                </h3>
                <p className="text-sm text-gray-600">
                  Read the agreement and check the vehicle markings before
                  signing. The signature and booking details are added as a
                  final page to the PDF.
                </p>
                <label className="block">
                  Signer’s full name
                  <input
                    aria-label="Signer full name"
                    maxLength={150}
                    value={signerName}
                    disabled={!editable}
                    onChange={(event) => setSignerName(event.target.value)}
                    className="block border rounded-lg p-3 w-full mt-2"
                  />
                </label>
                <div
                  className="border-2 border-dashed rounded-lg bg-gray-50"
                  style={{ aspectRatio: "4/1" }}
                >
                  <AgreementInk
                    signature
                    strokes={signature}
                    onChange={setSignature}
                    width={800}
                    height={200}
                    disabled={!editable}
                  />
                </div>
                <button
                  disabled={!editable}
                  onClick={() => setSignature([])}
                  className="text-sm underline text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
                >
                  Clear signature
                </button>
                <label className="flex gap-3 items-start">
                  <input
                    type="checkbox"
                    checked={consent}
                    disabled={!editable}
                    onChange={(event) => setConsent(event.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    I have read this agreement, confirm the marked vehicle
                    condition, and agree to sign electronically.
                  </span>
                </label>
                <p className="text-sm text-gray-600">
                  Finalizing permanently saves the PDF and emails it to{" "}
                  {record?.booking.customerEmail}. Signed agreements cannot be
                  changed.
                </p>
                <button
                  disabled={
                    !editable ||
                    !document ||
                    !!pdfError ||
                    !consent ||
                    !signerName.trim() ||
                    !signature.length ||
                    revision === null
                  }
                  onClick={finalize}
                  className="bg-primary text-white rounded-xl px-5 py-3 disabled:opacity-40 text-[13px] font-manrope-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:opacity-90"
                >
                  {signing
                    ? "Saving signed agreement…"
                    : "Sign & email agreement"}
                </button>
                {status === "signing" && (
                  <p role="status">
                    Finalizing this agreement. Please wait for its saved status.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
